/* eslint-disable react/react-in-jsx-scope */
import DemandeFinancement from "@/assets/icones/suivi-financement.svg?react";
import { useAuth } from "@/auth/hooks/useAuth";
import ErrorBoundary from "@/components/errors/ErrorBoundary";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { cn } from "@/libs/utils";
import { useCustomTheme, useLogoStore } from "@/store/store";
import {
  Award,
  FolderIcon,
  LayoutDashboard,
  MessagesSquare,
  Paintbrush,
} from "lucide-react";
import { FC, ReactNode, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import TreeView from "./TreeView";
import TreeViewDocument from "./TreeViewDocument";
type LeftbarProps = {
  handleToggleLeftbar: (val: boolean) => void;
  isOpen: boolean;
};
const Leftbar: FC<LeftbarProps> = ({ handleToggleLeftbar, isOpen }) => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "leftbar",
  });
  const { firstname } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const urlCurrent = location.pathname.endsWith("/")
    ? location.pathname.slice(0, -1)
    : location.pathname;
  const [docIsOpen, setDocIsOpen] = useState(false);
  const { isVisible, setIsVisible } = useCustomTheme();
  const { setLogoUrl } = useLogoStore();
  useEffect(() => {
    if (user?.logo?.uri) {
      setLogoUrl(user.logo.uri);
    }
  }, [user, setLogoUrl]);

  if (!firstname) {
    return <LoadingSpinner />;
  }

  return (
    <div
      id="sidebar"
      className={`flex flex-1 flex-col min-h-0 overflow-y-auto ${
        isOpen ? "pl-7 pr-2" : "flex flex-col items-center px-2"
      } bg-cover bg-center`}
    >
      <ul>
        <LinkNav
          isOpen={isOpen}
          picto={<LayoutDashboard size={18} />}
          link="/"
          isActive={urlCurrent == "/dashboard"}
          urlPref={firstname}
        >
          {t("Tableau de bord")}
        </LinkNav>
      </ul>
      {isOpen ? (
        <Suspense fallback={<LoadingSpinner />}>
          <ErrorBoundary>
            <div className="relative ml-[-22px]">
              <TreeView />
            </div>
          </ErrorBoundary>
        </Suspense>
      ) : (
        <button className="mb-4" onClick={() => handleToggleLeftbar(true)}>
          <FolderIcon className="flex size-5 opacity-70" />{" "}
        </button>
      )}
      <ul>
        <LinkNav
          isOpen={isOpen}
          picto={
            <DemandeFinancement
              size={18}
              className="relative bottom-0.5 fill-muted-foreground"
            />
          }
          link="/suivi-contribution"
          title={t("Suivi de contribution")}
          isActive={urlCurrent.includes("suivi-contribution")}
          urlPref={firstname}
        >
          {t("Suivi de contribution")}
        </LinkNav>
      </ul>
      {isOpen ? (
        <Suspense fallback={<LoadingSpinner />}>
          <ErrorBoundary>
            <div className="relative ml-[-22px]">
              <TreeViewDocument />
            </div>
          </ErrorBoundary>
        </Suspense>
      ) : (
        <button className="mb-4" onClick={() => handleToggleLeftbar(true)}>
          <FolderIcon className="flex size-5 opacity-70" />{" "}
        </button>
      )}
      <ul>
        <LinkNav
          isOpen={isOpen}
          picto={<Award size={18} />}
          link="/mes-certificats"
          title="Mes certificats"
          isActive={urlCurrent.includes("mes-certificats")}
          urlPref={firstname}
        >
          {t("Mes certificats")}
        </LinkNav>

        <LinkNav
          isOpen={isOpen}
          picto={<MessagesSquare size={18} />}
          link="/message"
          title="Bientôt disponible"
          className="pointer-events-none opacity-45"
          isActive={urlCurrent == "/dashboard/message"}
          urlPref={firstname}
        >
          {t("Messages")}
        </LinkNav>
        <div
          className={`line-clamp-1 flex flex-wrap items-center gap-2 text-left text-[13px] leading-4 cursor-pointer text-muted-foreground  ${
            !isOpen ? "mb-3  justify-center" : "mb-3"
          }
        `}
          onClick={() => setIsVisible(!isVisible)}
        >
          <Paintbrush size={18} /> {isOpen && "Personnalisation"}
        </div>
      </ul>
    </div>
  );
};

const LinkNav = ({
  isActive,
  isOpen,
  link,
  title,
  picto,
  children,
  className,
  urlPref,
}: {
  isActive?: boolean;
  isOpen: boolean;
  link: string;
  title?: string;
  picto?: ReactNode;
  children: ReactNode;
  className?: string;
  urlPref: string;
}) => {
  return (
    <li className={cn("mb-4", className)} title={title}>
      <Link
        to={`/${urlPref}${link}`}
        className={`line-clamp-1 flex flex-wrap items-center gap-2 text-left text-[13px] leading-4 hover:text-primary group ${
          isActive ? "text-primary font-medium" : "text-muted-foreground"
        } ${!isOpen ? "mb-3  justify-center" : "mb-3"}
        `}
        title={children?.toString()}
      >
        {picto}{" "}
        {isOpen && (
          <span className="truncate" style={{ width: "calc(100% - 30px)" }}>
            {children}
          </span>
        )}
      </Link>
    </li>
  );
};
export default Leftbar;
